/**
 * Vue 3 Composable for Modal Management
 * Consolidates 15+ modal functions from vanilla JS codebase
 * Provides reactive state and TypeScript-safe modal handling
 */

import { ref, computed, onUnmounted, type Ref } from 'vue'

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/**
 * Available modal types in the application
 */
export type ModalType =
  | 'create-game'
  | 'fill-bots'
  | 'player-customization'
  | 'opening-requirements'
  | 'round-end'
  | 'seat-selection'
  | 'join-request'
  | 'reconnection-request'
  | 'move-player'
  | 'spectator-list'
  | 'permission-help'
  | 'profile-edit'
  | 'how-to-play'
  | 'report-issue'
  | 'player-left'
  | 'restart-approval'
  | 'waiting-approval'
  | 'pickup-foot'
  | 'rule-violation'
  | 'go-out-confirmation'
  | 'idle-warning'
  | 'kick-player'
  | 'creator-action-request'
  | 'confirm-end-round'
  | 'confirm-end-game'
  | 'confirm-leave-game'
  | 'generic'

/**
 * Modal configuration options
 */
export interface ModalOptions {
  /** Whether the modal can be closed by clicking outside or pressing ESC */
  closable?: boolean
  /** Whether to show the close button in the modal header */
  showCloseButton?: boolean
  /** Custom CSS class to add to the modal */
  className?: string
  /** Callback when modal is closed */
  onClose?: () => void
  /** Callback when modal is opened */
  onOpen?: () => void
  /** Maximum width of the modal (CSS value) */
  maxWidth?: string
  /** Whether to add the modal to the DOM immediately */
  immediate?: boolean
}

/**
 * Modal state interface
 */
export interface ModalState {
  /** The currently open modal type (null if no modal is open) */
  currentModal: ModalType | null
  /** Data/context passed to the current modal */
  modalData: any
  /** Stack of modals (for nested modals) */
  modalStack: Array<{ type: ModalType; data: any; options: ModalOptions }>
  /** Modal options */
  options: ModalOptions
  /** Whether a modal is currently transitioning (opening/closing) */
  transitioning: boolean
}

/**
 * Player customization data
 */
export interface PlayerCustomizationData {
  displayName: string
  profilePicture: string
}

/**
 * Seat selection data
 */
export interface SeatSelectionData {
  seats: Array<{
    seat: number
    occupied: boolean
    player?: string
    is_bot?: boolean
  }>
  teams: Record<string, any>
  room_code: string
  player_to_assign?: {
    id: string
    username: string
    socket_id: string
  }
}

/**
 * Join/Reconnection request data
 */
export interface JoinRequestData {
  sid: string
  username: string
  display_name?: string
  profile_picture?: string
  user_id?: string
}

/**
 * Round end data
 */
export interface RoundEndData {
  team_a: {
    round_score: number
    total_score: number
  }
  team_b: {
    round_score: number
    total_score: number
  }
  winner?: string
  is_game_end?: boolean
}

/**
 * Spectator list data
 */
export interface SpectatorListData {
  spectators: Array<{
    id: string
    username: string
    display_name?: string
    profile_picture?: string
  }>
}

// ============================================================================
// Default Options
// ============================================================================

const DEFAULT_OPTIONS: ModalOptions = {
  closable: true,
  showCloseButton: true,
  className: '',
  maxWidth: '600px',
  immediate: false,
}

// ============================================================================
// Main Composable
// ============================================================================

/**
 * Vue 3 Composable for modal management
 * Provides reactive state and functions for opening, closing, and managing modals
 *
 * @example
 * ```ts
 * const { openModal, closeModal, isModalOpen, currentModal } = useModal()
 *
 * // Open a modal with data
 * openModal('player-customization', { username: 'Player1' })
 *
 * // Check if a specific modal is open
 * if (isModalOpen('create-game')) {
 *   console.log('Create game modal is open')
 * }
 *
 * // Close the current modal
 * closeModal()
 * ```
 */
export function useModal() {
  // ============================================================================
  // Reactive State
  // ============================================================================

  /**
   * Current modal state
   */
  const state: Ref<ModalState> = ref({
    currentModal: null,
    modalData: null,
    modalStack: [],
    options: { ...DEFAULT_OPTIONS },
    transitioning: false,
  })

  /**
   * Computed: Whether any modal is currently open
   */
  const hasOpenModal = computed(() => state.value.currentModal !== null)

  /**
   * Computed: Number of modals in the stack
   */
  const modalCount = computed(() => state.value.modalStack.length)

  /**
   * Computed: Whether the current modal is closable
   */
  const isClosable = computed(() => state.value.options.closable ?? true)

  // ============================================================================
  // Core Modal Functions
  // ============================================================================

  /**
   * Open a modal with optional data and options
   *
   * @param type - The type of modal to open
   * @param data - Optional data to pass to the modal
   * @param options - Optional configuration options
   *
   * @example
   * ```ts
   * // Simple modal
   * openModal('how-to-play')
   *
   * // Modal with data
   * openModal('join-request', {
   *   sid: '123',
   *   username: 'Player1',
   *   display_name: 'John Doe'
   * })
   *
   * // Modal with options
   * openModal('round-end', roundData, {
   *   closable: false,
   *   onClose: () => console.log('Modal closed')
   * })
   * ```
   */
  function openModal(
    type: ModalType,
    data: any = null,
    options: ModalOptions = {}
  ): void {
    // Merge options with defaults
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options }

    // If a modal is already open, push it to the stack
    if (state.value.currentModal) {
      state.value.modalStack.push({
        type: state.value.currentModal,
        data: state.value.modalData,
        options: state.value.options,
      })
    }

    // Set transitioning state
    state.value.transitioning = true

    // Set new modal state
    state.value.currentModal = type
    state.value.modalData = data
    state.value.options = mergedOptions

    // Call onOpen callback if provided
    if (mergedOptions.onOpen) {
      mergedOptions.onOpen()
    }

    // Clear transitioning after animation
    setTimeout(() => {
      state.value.transitioning = false
    }, 300) // Match typical CSS transition duration
  }

  /**
   * Close the current modal
   * If there are modals in the stack, restore the previous modal
   *
   * @param data - Optional data to return when closing (useful for modal results)
   * @returns The data passed to closeModal, or undefined
   *
   * @example
   * ```ts
   * // Simple close
   * closeModal()
   *
   * // Close with result data
   * const result = closeModal({ confirmed: true })
   * ```
   */
  function closeModal(data?: any): any {
    if (!state.value.currentModal) {
      return data
    }

    // Set transitioning state
    state.value.transitioning = true

    // Call onClose callback if provided
    if (state.value.options.onClose) {
      state.value.options.onClose()
    }

    // Store the return data
    const returnData = data

    // Check if there are modals in the stack
    if (state.value.modalStack.length > 0) {
      // Restore previous modal from stack
      const previousModal = state.value.modalStack.pop()!
      state.value.currentModal = previousModal.type
      state.value.modalData = previousModal.data
      state.value.options = previousModal.options
    } else {
      // Clear modal state
      state.value.currentModal = null
      state.value.modalData = null
      state.value.options = { ...DEFAULT_OPTIONS }
    }

    // Clear transitioning after animation
    setTimeout(() => {
      state.value.transitioning = false
    }, 300)

    return returnData
  }

  /**
   * Close all modals and clear the stack
   *
   * @example
   * ```ts
   * closeAllModals()
   * ```
   */
  function closeAllModals(): void {
    // Call onClose for current modal if exists
    if (state.value.currentModal && state.value.options.onClose) {
      state.value.options.onClose()
    }

    // Clear everything
    state.value.currentModal = null
    state.value.modalData = null
    state.value.modalStack = []
    state.value.options = { ...DEFAULT_OPTIONS }
    state.value.transitioning = false
  }

  /**
   * Check if a modal is currently open
   * If type is provided, checks if that specific modal is open
   *
   * @param type - Optional modal type to check
   * @returns True if the modal is open
   *
   * @example
   * ```ts
   * // Check if any modal is open
   * if (isModalOpen()) {
   *   console.log('A modal is open')
   * }
   *
   * // Check if a specific modal is open
   * if (isModalOpen('create-game')) {
   *   console.log('Create game modal is open')
   * }
   * ```
   */
  function isModalOpen(type?: ModalType): boolean {
    if (type) {
      return state.value.currentModal === type
    }
    return state.value.currentModal !== null
  }

  /**
   * Get the current modal type
   *
   * @returns The current modal type, or null if no modal is open
   */
  function getCurrentModal(): ModalType | null {
    return state.value.currentModal
  }

  /**
   * Get the data for the current modal
   * Type-safe data access with optional type parameter
   *
   * @returns The modal data, or null if no modal is open
   *
   * @example
   * ```ts
   * const data = getModalData<JoinRequestData>()
   * if (data) {
   *   console.log(data.username)
   * }
   * ```
   */
  function getModalData<T = any>(): T | null {
    return state.value.modalData as T | null
  }

  /**
   * Update the current modal's data
   * Useful for modals that need to update their state
   *
   * @param data - The new data to set
   *
   * @example
   * ```ts
   * updateModalData({ step: 2, formData: { name: 'John' } })
   * ```
   */
  function updateModalData(data: any): void {
    if (state.value.currentModal) {
      state.value.modalData = data
    }
  }

  /**
   * Replace the current modal with a new one
   * Useful for modal flows (e.g., create-game -> fill-bots)
   *
   * @param type - The new modal type
   * @param data - Optional data for the new modal
   * @param options - Optional configuration options
   *
   * @example
   * ```ts
   * // Replace current modal with a different one
   * replaceModal('fill-bots', { emptySlots: 2 })
   * ```
   */
  function replaceModal(
    type: ModalType,
    data: any = null,
    options: ModalOptions = {}
  ): void {
    // Close current modal without restoring from stack
    if (state.value.currentModal) {
      if (state.value.options.onClose) {
        state.value.options.onClose()
      }
      // Don't push to stack since we're replacing
    }

    // Open new modal
    const mergedOptions = { ...DEFAULT_OPTIONS, ...options }

    state.value.transitioning = true
    state.value.currentModal = type
    state.value.modalData = data
    state.value.options = mergedOptions

    if (mergedOptions.onOpen) {
      mergedOptions.onOpen()
    }

    setTimeout(() => {
      state.value.transitioning = false
    }, 300)
  }

  // ============================================================================
  // Utility Functions
  // ============================================================================

  /**
   * Check if the modal stack has any modals
   *
   * @returns True if there are modals in the stack
   */
  function hasModalStack(): boolean {
    return state.value.modalStack.length > 0
  }

  /**
   * Get the modal stack
   *
   * @returns Array of modals in the stack
   */
  function getModalStack(): Array<{
    type: ModalType
    data: any
    options: ModalOptions
  }> {
    return [...state.value.modalStack]
  }

  /**
   * Check if a specific modal type is anywhere in the stack
   *
   * @param type - Modal type to search for
   * @returns True if the modal is in the stack
   */
  function isModalInStack(type: ModalType): boolean {
    return state.value.modalStack.some((modal) => modal.type === type)
  }

  // ============================================================================
  // Promise-based Modal Helpers
  // ============================================================================

  /**
   * Open a modal and return a promise that resolves when the modal is closed
   * Useful for modals that need to return data (e.g., confirmation dialogs)
   *
   * @param type - The modal type to open
   * @param data - Optional data to pass to the modal
   * @param options - Optional configuration options
   * @returns Promise that resolves with the data passed to closeModal
   *
   * @example
   * ```ts
   * const result = await openModalPromise<PlayerCustomizationData>(
   *   'player-customization',
   *   { username: 'Player1' }
   * )
   * if (result) {
   *   console.log('User selected:', result.displayName)
   * }
   * ```
   */
  function openModalPromise<T = any>(
    type: ModalType,
    data: any = null,
    options: ModalOptions = {}
  ): Promise<T | null> {
    return new Promise((resolve) => {
      const originalOnClose = options.onClose

      openModal(type, data, {
        ...options,
        onClose: () => {
          if (originalOnClose) {
            originalOnClose()
          }
          // Resolve with the modal data (which may have been updated)
          resolve(getModalData<T>())
        },
      })
    })
  }

  // ============================================================================
  // Cleanup
  // ============================================================================

  /**
   * Clean up on component unmount
   * Closes all modals and clears the stack
   */
  try {
    onUnmounted(() => {
      closeAllModals()
    })
  } catch (e) {
    // Not in component context (e.g., during tests)
    // Cleanup will need to be done manually
  }

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // Reactive state
    state,
    currentModal: computed(() => state.value.currentModal),
    modalData: computed(() => state.value.modalData),
    hasOpenModal,
    modalCount,
    isClosable,
    transitioning: computed(() => state.value.transitioning),

    // Core functions
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    getCurrentModal,
    getModalData,
    updateModalData,
    replaceModal,

    // Utility functions
    hasModalStack,
    getModalStack,
    isModalInStack,

    // Promise-based helpers
    openModalPromise,
  }
}

/**
 * Singleton instance for global modal state
 * Use this to access modal state across components without prop drilling
 *
 * @example
 * ```ts
 * import { modalState } from '@/composables/useModal'
 *
 * // Access from any component
 * if (modalState.currentModal.value === 'create-game') {
 *   console.log('Create game modal is open')
 * }
 * ```
 */
let globalModalInstance: ReturnType<typeof useModal> | null = null

export function getGlobalModalInstance(): ReturnType<typeof useModal> {
  if (!globalModalInstance) {
    globalModalInstance = useModal()
  }
  return globalModalInstance
}

export const modalState = {
  get currentModal() {
    return getGlobalModalInstance().currentModal
  },
  get modalData() {
    return getGlobalModalInstance().modalData
  },
  get hasOpenModal() {
    return getGlobalModalInstance().hasOpenModal
  },
}
